'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';

// Assets
import { ChevronDown } from '../../../assets/icons/chevron-down';

// Lib
import { cn } from '../../../lib';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef(({ ...props }, ref) => (
  <div className="truncate">
    <SelectPrimitive.Value ref={ref} {...props} />
  </div>
));
SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectIcon = React.forwardRef(({ children, ...props }, ref) => (
  <SelectPrimitive.Icon ref={ref} asChild {...props}>
    {children}
  </SelectPrimitive.Icon>
));
SelectIcon.displayName = SelectPrimitive.Icon.displayName;

const SelectChevron = () => {
  return (
    <div className="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-[#e3f4f7]">
      <ChevronDown className="size-2.5 text-[#0099B7] transition group-disabled:opacity-40 group-data-[state=open]:rotate-180" />
    </div>
  );
};

const SelectTrigger = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        'group flex h-[57px] w-full items-center justify-between gap-2 rounded-md border border-[#e3e3e3] bg-white px-3.5 py-[10.5px] font-bold text-primary transition hover:border-[#e3e3e3] focus-visible:border-[#D2E4EA] focus-visible:outline-none disabled:pointer-events-none disabled:border-[#ECF6FA] disabled:bg-[#f2f2f2] disabled:text-primary-200 aria-[invalid="true"]:!border-red-500 data-[placeholder]:text-[#B8D0D8] disabled:data-[placeholder]:text-[#99a8b3]',
        className,
      )}
      {...props}
    >
      {children}
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef(
  ({ className, children, position = 'popper', ...props }, ref) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'relative z-50 max-h-80 w-[var(--radix-select-trigger-width)] overflow-y-auto overflow-x-hidden rounded-lg bg-white shadow-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'px-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full',
          )}
          style={{
            overflow: 'visible',
            scrollbarGutter: 'auto',
          }}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div className="group">
    <SelectPrimitive.Label
      ref={ref}
      className={cn('truncate px-4 py-2.5 text-sm font-bold', className)}
      {...props}
    />
    <SelectSeparator />
  </div>
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-gray-100 group-last:hidden', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div className="group first:pt-1 last:pb-1">
      <SelectPrimitive.Item
        ref={ref}
        className={cn(
          'w-full cursor-pointer select-none truncate rounded-md p-4 outline-none transition focus:bg-disabled-200 focus:font-bold focus:text-navy-500 data-[disabled]:pointer-events-none data-[state=checked]:font-bold data-[disabled]:text-disabled-400 data-[state=checked]:text-navy-500',
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
      <SelectSeparator />
    </div>
  ),
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectIcon,
  SelectChevron,
};
